import { inject } from '@angular/core';
import { ResolveFn, ActivatedRouteSnapshot } from '@angular/router';
import { map, catchError, of, tap, switchMap } from 'rxjs';
import { CommunityInitiativeService } from '../services/community-initiative/community-initiative.service';
import { CommunityService } from '../services/community/community.service';
import { TitleService } from '../services/meta/title.service';
import { IFileModel } from '../interfaces/file-model.interface';
import { environment as env } from '@env/environment';
import { ICommunityInitiativeModel } from '../interfaces/community-initiative.interface';
import { ICommunity } from '../interfaces/community.interface';

export const InitiativeMetaResolver: ResolveFn<boolean> = (route: ActivatedRouteSnapshot) => {
  const initiativeService = inject(CommunityInitiativeService);
  const communityService = inject(CommunityService);
  const titleService = inject(TitleService);

  const communityId = route.parent?.paramMap.get('id') || '';
  const initiativeId = route.paramMap.get('initiativeId');

  if (!initiativeId || !communityId) {
    return of(true);
  }

  return initiativeService.getInitiative(+initiativeId).pipe(
    switchMap((data) => {
      if (data) {
        // Initiative data available - set initiative meta tags
        setMetaTags(data, communityId, initiativeId, titleService);
        return of(true);
      } else {
        // Initiative is private/archived - fetch community data and set community meta tags
        return communityService.getCommunityById(+communityId).pipe(
          map((community) => {
            if (community) {
              setCommunityMetaTags(community, communityId, titleService);
            }
            return true;
          }),
          catchError(() => {
            console.error('Failed to load community data for meta tags');
            return of(true);
          })
        );
      }
    }),
    catchError((error) => {
      console.error('Error in initiative meta resolver:', error);
      // On error, try to fetch community data and set community meta tags as fallback
      return communityService.getCommunityById(+communityId).pipe(
        map((community) => {
          if (community) {
            setCommunityMetaTags(community, communityId, titleService);
          }
          return true;
        }),
        catchError(() => {
          console.error('Failed to load community data in error handler');
          return of(true);
        })
      );
    })
  );
};

function setMetaTags(
  data: ICommunityInitiativeModel,
  communityId: string,
  initiativeId: string,
  titleService: TitleService
): void {
  // Set page title
  const title = data.initiativeDisplayName
    ? `${data.initiativeDisplayName} | ${data.communityName}`
    : 'Community Initiative | SeeMyLegacy';
  titleService.updateTitle(title);
  const desc = `Learn about ${data.initiativeDisplayName}. Explore more details and find out how to get involved.`;
  titleService.updateDescription(desc);

  // Set OG URL for social media
  const initiativeUrl = `${env.webUrl}/community/${communityId}/initiative/${initiativeId}`;
  titleService.updateOgUrl(initiativeUrl);

  // Set OG image from cover image or first gallery image
  if (data.coverBlobUrl && data.coverBlobUrl !== '[]') {
    const coverBlobs = JSON.parse(data.coverBlobUrl) as IFileModel[];
    if (coverBlobs && coverBlobs.length > 0 && coverBlobs[0].BlobUrl) {
      titleService.updateOgImage(coverBlobs[0].BlobUrl);
    }
  } else if (data.blobUrls && data.blobUrls !== '[]') {
    const galleryBlobs = JSON.parse(data.blobUrls) as IFileModel[];
    if (galleryBlobs && galleryBlobs.length > 0 && galleryBlobs[0].BlobUrl) {
      titleService.updateOgImage(galleryBlobs[0].BlobUrl);
      console.log(galleryBlobs[0].BlobUrl)
    }
  }
}

function setCommunityMetaTags(
  community: ICommunity,
  communityId: string,
  titleService: TitleService
): void {
  // Set page title for community - matching campaigns component pattern
  const title = community.displayName
    ? `${community.displayName} | SeeMyLegacy`
    : 'Community | SeeMyLegacy';
  titleService.updateTitle(title);
  
  // Set description - matching campaigns component pattern
  const desc = `Explore ${community.displayName ?? 'this community'} and discover their initiatives, campaigns, and community stories.`;
  titleService.updateDescription(desc);

  // Set OG URL for community
  const communityUrl = `${env.webUrl}/community/${communityId}`;
  titleService.updateOgUrl(communityUrl);

  // Set OG image from community profile image blob - matching campaigns component pattern
  if (community.profileImageBlob) {
    const images = JSON.parse(community.profileImageBlob) as IFileModel[];
    if (images && images.length > 0 && images[0].BlobUrl) {
      titleService.updateOgImage(images[0].BlobUrl);
    }
  }
}
